// @ts-nocheck
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import ShadcnHorizontalChart from '@/components/shared/charts/ShadcnHorizontalChart'
import NoData from '@/components/shared/NoData'
import { SegmentedControl } from '@/components/shared/layout'
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowRight01Icon } from "@hugeicons/core-free-icons"
import {
  OVERVIEW_BREAKDOWN_LIMIT,
  OVERVIEW_LIST_CLASS,
} from '@/lib/constants/analyticsOverview'
import {
  OverviewBreakdownCard,
  getPeriodUniqueVisitors,
} from '@/components/shared/overview/OverviewBreakdownCard'

const PAGE_MODE_OPTIONS = [
  { value: 'Page' as const, label: 'All' },
  { value: 'Entry Page' as const, label: 'Landing' },
  { value: 'Exit Page' as const, label: 'Exit' },
]

// Loading skeleton for horizontal bar chart
function BarChartSkeleton() {
  return (
    <div className="space-y-3 py-2">
      {[0.95, 0.72, 0.58, 0.45, 0.32].map((width, i) => (
        <div 
          key={i}
          className="flex items-center gap-3"
          style={{ 
            animationDelay: `${i * 100}ms`,
            animation: 'fadeInUp 0.4s ease-out forwards',
            opacity: 0
          }}
        >
          <div className="flex-1 flex items-center gap-3">
            <div 
              className="h-8 rounded-md bg-gradient-to-r from-muted/80 via-muted/40 to-muted/80 animate-shimmer"
              style={{ 
                width: `${width * 100}%`,
                backgroundSize: '200% 100%'
              }}
            />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="h-4 w-12 rounded bg-muted/60 animate-pulse" />
            <div className="h-4 w-10 rounded bg-muted/40 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  )
}

interface PageSession {
  page: string
  count: string
}

interface PageEntry {
  page: string
  entry_count: string
}

interface PageExit {
  page: string
  entry_count: string
}

interface PagesComponentProps {
  dashBoardData: any
  onOpenModal?: (data: any) => void
  onFilterByParam?: (category: string, filter: string) => void
  className?: string
  /** Overview list-only: muted full-row bars */
  overviewMode?: boolean
}

export default function PagesComponent({
  dashBoardData,
  onOpenModal,
  onFilterByParam,
  className,
  overviewMode = false,
}: PagesComponentProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const [currentTab, setCurrentTab] = useState<'Page' | 'Entry Page' | 'Exit Page'>('Page')
  const [labels, setLabels] = useState<string[]>([])
  const [values, setValues] = useState<number[]>([])
  const [fullUrl, setFullUrl] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Get the base path for navigation
  const getBasePath = () => {
    if (params.workspaceId) {
      return `/workspace/${params.workspaceId}`;
    }
    if (params.propertySlugId) {
      return `/p/${params.propertySlugId}`;
    }
    if (params.domain) {
      return `/d/${params.domain}`;
    }
    return '';
  };
  const basePath = getBasePath();

  // Safe data extraction with defaults and type assertions
  // Use useMemo to stabilize array references and prevent infinite loops
  const pageViews = useMemo(() => dashBoardData?.metrics?.pages?.pageViews || [], [dashBoardData])
  const pageSessions = useMemo(() => (dashBoardData?.metrics?.pages?.pageSessions || []) as PageSession[], [dashBoardData])
  const entryPages = useMemo(() => {
    return (dashBoardData?.metrics?.entryPages || []) as PageEntry[];
  }, [dashBoardData])
  const exitPages = useMemo(() => (dashBoardData?.metrics?.exitPages || []) as PageExit[], [dashBoardData])
  const domain = dashBoardData?.metrics?.domain || ''

  // Filter pages based on type
  const filterPages = (data: any[]) => {
    if (!data) return []
    return data.filter(({ page }) => {
      if (!page) return false
      return true
    })
  }

  // Process data based on current tab
  useEffect(() => {
    let newLabels: string[] = []
    let newValues: number[] = []
    
    if (pageSessions.length > 0 && currentTab === 'Page') {
      const filteredPages = filterPages(pageSessions)
      const sortedPages = [...filteredPages].sort(
        (a, b) => Number(b.count) - Number(a.count)
      )
      const n = overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 5
      newLabels = sortedPages.map((pageView) => pageView.page).slice(0, n)
      newValues = sortedPages.map((row) => Number(row.count)).slice(0, n)
    } else if (entryPages && currentTab === 'Entry Page') {
      const n = overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 5
      const sortedEntryPages = [...entryPages].sort(
        (a, b) => Number(b.entry_count) - Number(a.entry_count)
      )
      newLabels = sortedEntryPages.map((entryPage) => entryPage.page).slice(0, n)
      newValues = sortedEntryPages
        .map((entryPage) => Number(entryPage.entry_count))
        .slice(0, n)
    } else if (exitPages && currentTab === 'Exit Page') {
      const n = overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 5
      const sortedExitPages = [...exitPages].sort(
        (a, b) => Number(b.entry_count) - Number(a.entry_count)
      )
      newLabels = sortedExitPages.map((exitPage) => exitPage.page).slice(0, n)
      newValues = sortedExitPages
        .map((exitPage) => Number(exitPage.entry_count))
        .slice(0, n)
    }
    
    setLabels(newLabels)
    setValues(newValues)
    
    if (domain && newLabels.length > 0) {
      setFullUrl(newLabels.map((page) => domain.concat(page)))
    } else {
      setFullUrl([])
    }
    
    setIsLoading(false)
  }, [pageSessions, entryPages, exitPages, currentTab, domain, overviewMode])

  // Navigate to the full Pages tab instead of opening a modal
  const navigateToPages = () => {
    navigate(`${basePath}/pages${location.search}`)
  }

  const handleFilter = (category: string, title: string) => {
    if (onFilterByParam) {
      onFilterByParam(category, title)
    }
  }

  const renderBarChart = () => {
    if (labels.length === 0) {
      return <NoData />
    }

    return (
      <ShadcnHorizontalChart
        labels={labels}
        values={values}
        maxItems={overviewMode ? OVERVIEW_BREAKDOWN_LIMIT : 5}
        onBarClick={(label) => handleFilter(currentTab, label)}
        title="Page"
        valueLabel={currentTab === 'Page' ? 'Visitors' : currentTab === 'Entry Page' ? 'Entries' : 'Exits'}
        variant="underline"
        showFavicons={false}
      />
    )
  }

  if (overviewMode) {
    return (
      <OverviewBreakdownCard
        className={className}
        title="Pages"
        tabs={PAGE_MODE_OPTIONS}
        tab={currentTab}
        onTabChange={setCurrentTab}
        dimensionLabel="Page"
        valueLabel={currentTab === 'Page' ? 'Visitors' : currentTab === 'Entry Page' ? 'Entries' : 'Exits'}
        rows={labels.map((label, i) => ({ label, value: values[i] ?? 0 }))}
        total={getPeriodUniqueVisitors(dashBoardData?.metrics)}
        limit={5}
        isLoading={isLoading || !dashBoardData?.metrics}
        onRowClick={(page) => handleFilter(currentTab, page)}
        footerLabel="View all pages"
        onFooterClick={navigateToPages}
      />
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="border-b border-border/40 pb-3">
        <div className="flex items-center justify-between gap-3 w-full">
          <span className="text-sm font-medium text-foreground shrink-0">Pages</span>
          <div className="flex items-center gap-2 min-w-0">
            <SegmentedControl
              options={PAGE_MODE_OPTIONS}
              value={currentTab}
              onChange={(v) => setCurrentTab(v)}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={navigateToPages}
              className="shrink-0"
            >
              View all
              <HugeiconsIcon icon={ArrowRight01Icon} size={14} className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading || !dashBoardData?.metrics ? (
          <BarChartSkeleton />
        ) : labels.length === 0 ? (
          <div className="dashboard-empty">
            No data to display. Try a different date range.
          </div>
        ) : (
          <div className={overviewMode ? OVERVIEW_LIST_CLASS : "w-full flex-1 overflow-y-auto min-h-0"}>
            {renderBarChart()}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
